import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Button,
  Container
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  LocalHospital as LocalHospitalIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  MedicalServices as MedicalServicesIcon,
  Healing as HealingIcon,
  ReportProblem as ReportProblemIcon,
  LocationOn as LocationOnIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon
} from '@mui/icons-material';

const getNivelTriageInfo = (nivel) => {
  const info = {
    'ESI-1': {
      color: '#FF4B4B',
      background: 'linear-gradient(145deg, rgba(255, 75, 75, 0.15), rgba(255, 75, 75, 0.05))',
      glassBg: 'rgba(255, 75, 75, 0.1)',
      borderGlow: '0 0 15px rgba(255, 75, 75, 0.3)',
      texto: 'Alta Complejidad',
      tiempo: 'Atención Inmediata',
      accion: 'Requiere atención médica inmediata',
    },
    'ESI-2': {
      color: '#FF8F3E',
      background: 'linear-gradient(145deg, rgba(255, 143, 62, 0.15), rgba(255, 143, 62, 0.05))',
      glassBg: 'rgba(255, 143, 62, 0.1)',
      borderGlow: '0 0 15px rgba(255, 143, 62, 0.3)',
      texto: 'Emergencia',
      tiempo: 'Atención en 15 minutos',
      accion: 'Diríjase a urgencias inmediatamente',
    },
    'ESI-3': {
      color: '#FFB302',
      background: 'linear-gradient(145deg, rgba(255, 179, 2, 0.15), rgba(255, 179, 2, 0.05))',
      glassBg: 'rgba(255, 179, 2, 0.1)',
      borderGlow: '0 0 15px rgba(255, 179, 2, 0.3)',
      texto: 'Mediana Complejidad',
      tiempo: 'Atención en 90 minutos',
      accion: 'Su condición requiere atención en un servicio de urgencias',
    },
    'ESI-4': {
      color: '#4ADE80',
      background: 'linear-gradient(145deg, rgba(74, 222, 128, 0.15), rgba(74, 222, 128, 0.05))',
      glassBg: 'rgba(74, 222, 128, 0.1)',
      borderGlow: '0 0 15px rgba(74, 222, 128, 0.3)',
      texto: 'Baja Complejidad',
      tiempo: 'Atención en 180 minutos',
      accion: 'Puede esperar atención médica',
    },
    'ESI-5': {
      color: '#3B82F6',
      background: 'linear-gradient(145deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))',
      glassBg: 'rgba(59, 130, 246, 0.1)',
      borderGlow: '0 0 15px rgba(59, 130, 246, 0.3)',
      texto: 'Sin Complejidad',
      tiempo: 'Atención programada',
      accion: 'Puede agendar una consulta regular',
    }
  };
  return info[nivel] || info['ESI-3'];
};

const ResultadoAnalisis = ({ resultado, tipo }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    if (resultado?.audioUrl) {
      const audio = audioRef.current;
      audio.src = resultado.audioUrl;
      
      return () => {
        audio.pause();
        audio.src = '';
      };
    }
  }, [resultado?.audioUrl]);

  if (!resultado || !resultado.analisis) return null;

  // Mejorar la extracción del nivel ESI
  const nivelMatch = resultado.analisis.match(/NIVEL DE URGENCIA:[\s\n]*(ESI-[1-5])/);
  const nivel = nivelMatch ? nivelMatch[1] : null;
  
  if (!nivel) {
    console.error('No se pudo extraer el nivel ESI del análisis:', resultado.analisis);
  }
  
  const triageInfo = getNivelTriageInfo(nivel || 'ESI-3');

  const sections = {
    transcripcion: tipo === 'audio' ? resultado.transcripcion : resultado.texto,
    signosAlarma: resultado.analisis.match(/SIGNOS DE ALARMA:(.*?)(?=\n\n|$)/s)?.[1]
      ?.split('-')
      .filter(item => item.trim())
      .map(item => item.trim()) || [],
    diagnostico: resultado.analisis.match(/DIAGNÓSTICO PRELIMINAR:(.*?)(?=\n\n|$)/s)?.[1]?.trim() || '',
    sintomas: resultado.analisis.match(/SÍNTOMAS IDENTIFICADOS:(.*?)(?=\n\n|$)/s)?.[1]
      ?.split('-')
      .filter(item => item.trim())
      .map(item => item.trim()) || [],
    recomendaciones: resultado.analisis.match(/RECOMENDACIONES:(.*?)(?=\n\n|$)/s)?.[1]
      ?.split('-')
      .filter(item => item.trim())
      .map(item => item.trim()) || [],
    centroRecomendado: resultado.analisis.match(/CENTRO RECOMENDADO:(.*?)(?=\n\n|$)/s)?.[1]?.trim() || ''
  };

  const handlePlayPause = () => {
    if (audioRef.current.readyState >= 2) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } else {
      console.error('El audio no está listo para ser reproducido.');
    }
  };

  const leerResultado = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      setIsPlaying(false);
    } else {
      const mensaje = `
        ${triageInfo.texto}. 
        ${triageInfo.tiempo}. 
        ${sections.diagnostico}
        Síntomas identificados: ${sections.sintomas.join(', ')}
        Recomendaciones: ${sections.recomendaciones.join(', ')}
      `;
      const utterance = new SpeechSynthesisUtterance(mensaje);
      utterance.lang = 'es-CL';
      utterance.onend = () => setIsPlaying(false);
      speechSynthesis.speak(utterance);
      setIsPlaying(true);
    }
  };

  return (
    <Fade in>
      <Box>
        {/* Transcripción Desplegable */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease'
          }}
        >
          <Box 
            onClick={() => setExpanded(!expanded)}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AssignmentIcon 
                sx={{ 
                  fontSize: 28,
                  color: theme.palette.primary.main,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} 
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {tipo === 'audio' ? 'Transcripción' : 'Texto Extraído'}
              </Typography>
            </Box>
            <IconButton sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  background: 'rgba(255, 255, 255, 0.5)',
                  p: 2,
                  borderRadius: '12px'
                }}
              >
                {sections.transcripcion}
              </Typography>
              {tipo === 'audio' && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <IconButton
                    onClick={handlePlayPause}
                    sx={{
                      background: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        background: theme.palette.primary.dark,
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* Nivel de Triaje y Diagnóstico */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 3,
            background: triageInfo.background,
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: `1px solid ${triageInfo.color}20`,
            boxShadow: triageInfo.borderGlow,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
              pointerEvents: 'none'
            }
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative'
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    mb: 2,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80px',
                      height: '80px',
                      background: triageInfo.glassBg,
                      borderRadius: '50%',
                      filter: 'blur(15px)',
                      zIndex: 0
                    }
                  }}
                >
                  <MedicalServicesIcon sx={{ 
                    fontSize: 48, 
                    color: triageInfo.color,
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                  }} />
                </Box>
                <Chip
                  label={nivel}
                  sx={{
                    background: `linear-gradient(135deg, ${triageInfo.color}, ${triageInfo.color}dd)`,
                    color: 'white',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    mb: 2,
                    p: 2,
                    height: 'auto',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: triageInfo.color,
                    fontWeight: 600,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {triageInfo.texto}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mt: 2,
                  p: 1,
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <AccessTimeIcon sx={{ color: triageInfo.color }} />
                  <Typography variant="body2" sx={{ color: triageInfo.color, fontWeight: 500 }}>
                    {triageInfo.tiempo}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Box sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <HealingIcon sx={{ 
                      color: triageInfo.color,
                      fontSize: 28,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Diagnóstico Preliminar
                    </Typography>
                  </Box>
                  <Tooltip title="Reproducir/Detener diagnóstico">
                    <IconButton 
                      onClick={leerResultado}
                      sx={{
                        background: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(5px)',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.2)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isPlaying ? <PauseIcon sx={{ color: triageInfo.color }} /> : <PlayArrowIcon sx={{ color: triageInfo.color }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="body1"
                  sx={{
                    background: 'rgba(255,255,255,0.1)',
                    p: 2,
                    borderRadius: '12px',
                    backdropFilter: 'blur(5px)',
                    flex: 1,
                    lineHeight: 1.6
                  }}
                >
                  {sections.diagnostico}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Signos de Alarma */}
        {sections.signosAlarma.length > 0 && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3,
              background: 'linear-gradient(145deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              boxShadow: '0 0 15px rgba(239, 68, 68, 0.2)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 2,
              pb: 2,
              borderBottom: '1px solid rgba(239, 68, 68, 0.2)'
            }}>
              <ReportProblemIcon sx={{ 
                color: '#EF4444',
                fontSize: 28,
                filter: 'drop-shadow(0 2px 4px rgba(239, 68, 68, 0.3))'
              }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Signos de Alarma
              </Typography>
            </Box>
            <List dense sx={{ pt: 1 }}>
              {sections.signosAlarma.map((signo, index) => (
                <ListItem 
                  key={index}
                  sx={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(5px)',
                    borderRadius: '12px',
                    mb: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      background: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <ErrorOutlineIcon sx={{ color: '#EF4444' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={signo} 
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Síntomas Identificados */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 2,
                pb: 2,
                borderBottom: '1px solid rgba(0,0,0,0.1)'
              }}>
                <LocalHospitalIcon sx={{ 
                  color: theme.palette.primary.main,
                  fontSize: 28,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Síntomas Identificados
                </Typography>
              </Box>
              <List dense>
                {sections.sintomas.map((sintoma, index) => (
                  <ListItem 
                    key={index}
                    sx={{
                      background: 'rgba(255,255,255,0.5)',
                      borderRadius: '12px',
                      mb: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(10px)',
                        background: 'rgba(255,255,255,0.7)'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <CircleIcon sx={{ 
                        color: theme.palette.primary.main, 
                        fontSize: 10,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={sintoma}
                      sx={{ 
                        '& .MuiListItemText-primary': {
                          fontWeight: 500
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Recomendaciones */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 2,
                pb: 2,
                borderBottom: '1px solid rgba(0,0,0,0.1)'
              }}>
                <InfoIcon sx={{ 
                  color: theme.palette.success.main,
                  fontSize: 28,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recomendaciones
                </Typography>
              </Box>
              <List dense>
                {sections.recomendaciones.map((recomendacion, index) => (
                  <ListItem 
                    key={index}
                    sx={{
                      background: 'rgba(255,255,255,0.5)',
                      borderRadius: '12px',
                      mb: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(10px)',
                        background: 'rgba(255,255,255,0.7)'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <CircleIcon sx={{ 
                        color: theme.palette.success.main, 
                        fontSize: 10,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={recomendacion}
                      sx={{ 
                        '& .MuiListItemText-primary': {
                          fontWeight: 500
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Centro Recomendado */}
        {sections.centroRecomendado && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mt: 3,
              background: 'linear-gradient(145deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mb: 2
            }}>
              <LocationOnIcon sx={{ 
                color: '#3B82F6',
                fontSize: 28,
                filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))'
              }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Centro Recomendado
              </Typography>
            </Box>
            <Typography 
              variant="body1"
              sx={{
                background: 'rgba(255,255,255,0.1)',
                p: 2,
                borderRadius: '12px',
                backdropFilter: 'blur(5px)',
                fontWeight: 500
              }}
            >
              {sections.centroRecomendado}
            </Typography>
          </Paper>
        )}
      </Box>
    </Fade>
  );
};

export default ResultadoAnalisis; 